import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Search, X } from "lucide-react";
import { mailboxStatus, mailboxScan, mailboxAdd } from "../lib/api";

const RANGES = [["30", "Last 30 days"], ["90", "Last 3 months"], ["365", "Last year"], ["1095", "Last 3 years"], ["0", "All mail"]];
const errText = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : d; };

// "Find customers in my mailbox": search the connected mailbox by keywords, review the people found, add the ones picked.
export default function MailboxModal({ close, onAdded }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ keywords: "", match: "any", days: "365", folders: "both" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);
  useEffect(() => { mailboxStatus().then(setStatus).catch(() => setStatus({ connected: false })); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const scan = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await mailboxScan(form);
      setResult(r); setPicked(new Set(r.people.filter(p => !p.exists).map(p => p.email))); setFilter("");
    } catch (e) { toast.error(errText(e, "Couldn't read the mailbox")); } finally { setBusy(false); }
  };
  const shown = useMemo(() => {
    const t = filter.toLowerCase().split(/\s+/).filter(Boolean);
    return (result?.people || []).filter(p => { const h = `${p.name} ${p.email} ${p.company} ${p.subjects.join(" ")}`.toLowerCase(); return t.every(x => h.includes(x)); });
  }, [result, filter]);
  const toggle = (email) => { const n = new Set(picked); n.has(email) ? n.delete(email) : n.add(email); setPicked(n); };
  const selectable = shown.filter(p => !p.exists);
  const allOn = selectable.length > 0 && selectable.every(p => picked.has(p.email));
  const toggleAll = () => { const n = new Set(picked); selectable.forEach(p => allOn ? n.delete(p.email) : n.add(p.email)); setPicked(n); };
  const add = async () => {
    setAdding(true);
    try {
      const r = await mailboxAdd(result.people.filter(p => picked.has(p.email)));
      toast.success(`Added ${r.added} contact${r.added === 1 ? "" : "s"}${r.already_there ? ` · ${r.already_there} already there` : ""}`);
      onAdded?.(); close();
    } catch (e) { toast.error(errText(e, "Couldn't add the contacts")); } finally { setAdding(false); }
  };

  return <div className="modal-backdrop" onClick={close}>
    <div className="modal modal-wide mailbox-modal" onClick={e => e.stopPropagation()} data-testid="mailbox-modal">
      <button className="modal-close" onClick={close} aria-label="Close"><X size={16} /></button>
      <div className="modal-icon"><Mail size={18} /></div>
      <h2>Find customers in your mailbox</h2>
      <p>Search your inbox and sent mail, then choose who to add to Contacts. Only senders and recipients are read, mail is never changed, and newsletters and no-reply senders are skipped.</p>
      {status && !status.connected && <div className="alert-strip">Connect your mailbox first: Settings → Email (host, username and app password).</div>}
      {status?.connected && <small className="muted">Mailbox: {status.account} ({status.host})</small>}
      <div className="mailbox-form">
        <label className="mb-wide">Words to look for <small>(separate with commas; leave empty to list everyone)</small>
          <input value={form.keywords} onChange={set("keywords")} onKeyDown={e => e.key === "Enter" && !busy && status?.connected && scan()} placeholder="e.g. quote, invoice, order, proposal" data-testid="mailbox-keywords" /></label>
        <label>Match<select value={form.match} onChange={set("match")}><option value="any">Any of the words</option><option value="all">All of the words</option></select></label>
        <label>Period<select value={form.days} onChange={set("days")}>{RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <label>Look in<select value={form.folders} onChange={set("folders")}><option value="both">Inbox and sent</option><option value="inbox">Inbox only</option><option value="sent">Sent only</option></select></label>
        <button className="primary-button mb-go" onClick={scan} disabled={busy || !status?.connected} data-testid="mailbox-scan">{busy ? <><Loader2 size={15} className="spin" /> Reading mail…</> : <><Search size={15} /> Search mailbox</>}</button>
      </div>
      {result && <div className="mailbox-results" data-testid="mailbox-results">
        <div className="mb-bar">
          <b>{result.total} {result.total === 1 ? "person" : "people"} found</b><small className="muted">in {result.scanned} matching emails</small>
          <div className="inline-search"><Search size={14} /><input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter these results" aria-label="Filter results" /></div>
        </div>
        {result.total === 0 ? <p className="muted">Nobody matched. Try other words or a longer period.</p> : <div className="mb-list">
          <label className="mb-row mb-head"><input type="checkbox" checked={allOn} onChange={toggleAll} /><span>Select all {selectable.length} shown</span></label>
          {shown.map(p => <label key={p.email} className={`mb-row${p.exists ? " mb-exists" : ""}`}>
            <input type="checkbox" checked={picked.has(p.email)} disabled={p.exists} onChange={() => toggle(p.email)} />
            <span className="mb-who"><b>{p.name || p.email.split("@")[0]}</b><small>{p.email}{p.company ? ` · ${p.company}` : ""}</small></span>
            <span className="mb-what"><small>{p.subjects[0] || "—"}</small><small className="muted">{p.count} email{p.count === 1 ? "" : "s"} · last {p.last_date || "n/a"}</small></span>
            {p.exists && <span className="badge badge-green">In contacts</span>}
          </label>)}
        </div>}
        <div className="modal-actions">
          <span className="call-muted">{picked.size} selected</span>
          <button className="outline-button" onClick={close}>Cancel</button>
          <button className="primary-button" onClick={add} disabled={adding || picked.size === 0} data-testid="mailbox-add">{adding ? <Loader2 size={15} className="spin" /> : null} Add {picked.size || ""} to contacts</button>
        </div>
      </div>}
    </div>
  </div>;
}
